"use client"

import { motion, AnimatePresence } from "motion/react"
import { Twitter, Linkedin, Link2, Check } from "lucide-react"
import { useState } from "react"

import { Button } from "@/core/ui/button"

interface ShareButtonsProps {
    title: string
    url?: string // Optional, defaults to current window location
}

export function ShareButtons({ title, url }: ShareButtonsProps) {
    const [copied, setCopied] = useState(false)

    const getUrl = () => {
        if (typeof window !== "undefined") {
            return url || window.location.href
        }
        return ""
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(getUrl())
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    const handleShare = (platform: "twitter" | "linkedin") => {
        const currentUrl = encodeURIComponent(getUrl())
        const text = encodeURIComponent(title)

        let shareLink = ""
        if (platform === "twitter") {
            shareLink = `https://twitter.com/intent/tweet?text=${text}&url=${currentUrl}`
        } else if (platform === "linkedin") {
            shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${currentUrl}`
        }

        window.open(shareLink, "_blank", "width=600,height=400")
    }

    return (
        <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 font-medium mr-2">Share:</span>

            <Button
                variant="outline"
                size="icon"
                onClick={() => handleShare("twitter")}
                className="h-9 w-9 rounded-full border-white/10 bg-white/5 hover:bg-[#1DA1F2]/20 hover:text-[#1DA1F2] hover:border-[#1DA1F2]/30 transition-all duration-300"
            >
                <Twitter className="h-4 w-4" />
            </Button>

            <Button
                variant="outline"
                size="icon"
                onClick={() => handleShare("linkedin")}
                className="h-9 w-9 rounded-full border-white/10 bg-white/5 hover:bg-[#0A66C2]/20 hover:text-[#0A66C2] hover:border-[#0A66C2]/30 transition-all duration-300"
            >
                <Linkedin className="h-4 w-4" />
            </Button>

            <Button
                variant="outline"
                size="icon"
                onClick={handleCopy}
                className="h-9 w-9 rounded-full border-white/10 bg-white/5 hover:bg-premium-cyan/20 hover:text-premium-cyan hover:border-premium-cyan/30 transition-all duration-300 relative"
            >
                <AnimatePresence mode="wait">
                    {copied ? (
                        <motion.div
                            key="check"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                        >
                            <Check className="h-4 w-4" />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="link"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                        >
                            <Link2 className="h-4 w-4" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </Button>
        </div>
    )
}
